import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Pet from '@spike/pet-model';
import Staff from 'model/Staff';
import { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import AddOrEditService from './AddOrEditService';
import CreateBookingHeader from '../CreateBookingHeader';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import FooterComponent from '../FooterComponent';
import {
    PetServiceWrapper,
    versionPetAndServices,
    Discount,
    Feeding,
    Medication,
    Daycare,
    DaycareData,
    Boarding,
    BoardingData,
    Transportation,
    TransportationData,
    Walking,
    Training,
    DataVaraible
} from 'model/NewMultipleBooking';
import {
    DaycareServiceWrapper,
    BoardingServiceWrapper,
    TransportationService,
    WalkingService,
    TrainingService
} from 'model/Service';
import useNonInitialEffect from '@versiondos/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import {
    fetchDaycareServicesThunk,
    fetchBoardingOffersThunk,
    fetchTransportationServicesThunk,
    fetchTrainingServicesThunk,
    fetchWalkingServicesThunk
} from 'actions/services/ServicesActions';
import { RootState } from 'store';
import { useApiClientWrapper, useMarketplace, useTimeZone } from 'hooks';
import useServiceCategories from 'hooks/useServiceCategories';
import { searchEnrollmentsThunk, MembershipsStatus } from '@spike/memberships-action';
import { fetchPetServicesThunk, NewBookingsStatus } from '@spike/new-bookings-action';
import { PetService } from '@spike/new-booking-model';
import type { PetCustomPrice } from '@spike/pet-model';
import { fetchPetCustomPricesThunk } from '@spike/pets-action';
import type { Enrollment, MembershipItem } from '@spike/membership-model';
import { TransportationType } from '../Transportation/UI/TransportationTypeSelector/TransportationTypeSelector';
import { ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector';
import type { MembershipsSelection } from '../Memberships/UI/MembershipsSelector/MembershipsSelector';
import {
    flowFromMembershipItemType,
    flowFromPetServiceBusinessArea,
    getTodayClosingTime,
    getTodayOpeningTime,
    membershipItemNeedsPetServicesLookup,
    parseMembershipServiceId,
    type MembershipDrivenServiceFlow
} from '../Memberships/utils/membershipItemFlow';
import { addTimeToDate } from 'utils/DateUtils';
import { ServicesStatus } from 'reducers/services/ServicesState';
import type { EnrollmentAnchor } from '../utils/enrollmentAnchor';
import {
    applyMembershipBoardingPricing,
    applyMembershipCatalogPriceWithPreviewDiscount,
    applyMembershipDaycarePricing,
    computeMembershipDisplayPrice,
    computeMembershipPreviewDiscount,
    resolveBoardingNightlyPriceFromService
} from '../utils/membershipDisplayPrice';
import { getBoardingCatalogNightlyPrice, getBoardingStandardNightlyPrice } from '../Boarding/utils/boardingPricing';
import { resolveDropoffPrice, resolvePickupPrice, resolveStandardPrice } from '../utils/bookingCustomPrices';

const DEFAULT_SERVICE_DISCOUNT: ValueTypeSelectorValue = { type: 'amount', value: 0 };

function resolveMembershipServiceDiscount(
    preview: ValueTypeSelectorValue | null,
    current?: ValueTypeSelectorValue
): ValueTypeSelectorValue {
    return preview ?? current ?? DEFAULT_SERVICE_DISCOUNT;
}

function buildEditBoardingData(
    service: PetServiceWrapper,
    from: Moment | undefined,
    to: Moment | undefined,
    roomUnitId?: number
): BoardingData | undefined {
    if (!from?.isValid()) {
        return undefined;
    }
    const stayTo = to?.isValid() ? to.clone() : from.clone().add(1, 'day');
    return {
        feedings: service.feedings ?? [],
        medications: service.medications ?? [],
        boarding: {
            from: from.clone(),
            to: stayTo,
            service: {
                serviceId: service.service.id,
                serviceName: service.service.name,
                price: service.price,
                duration: service.durationInMinutes ?? 0,
                durationService: service.duration,
                beforePrice: service.beforePrice ?? service.price,
                selectedOffer: service.selectedOffer
            } as BoardingServiceWrapper,
            room: service.room,
            roomUnitId: roomUnitId ?? service.roomUnitId,
            price: service.price,
            notes: service.notes
        }
    };
}

interface Props {
    parentId: number;
    petId?: number;
    appointmentId?: number;
    service?: PetServiceWrapper;
    staffId?: number;
    staffIdPreselected?: number;
    date?: Moment;
    versionPetAndServices?: versionPetAndServices;
    petsUsed?: Array<number>;
    servicesUsed?: Array<number>;
    discount: Discount;
    loading?: boolean;
    petWithoutSelect?: boolean;
    hideRemove?: boolean;
    dateEnd?: Moment;
    recurrentInfo?: boolean;
    nights?: number;
    flow?:
        | 'grooming'
        | 'dental'
        | 'daycare'
        | 'boarding'
        | 'transportation'
        | 'walking'
        | 'training'
        | 'memberships'
        | undefined;
    isEvaluation?: boolean;
    roomUnitId?: number;
    transportationType?: TransportationType;
    onBack: () => void;
    onBook: (
        dateAppt?: Moment,
        petIdAppt?: number,
        serviceAppt?: PetServiceWrapper,
        staffIdAppt?: number,
        discountAppt?: Discount,
        flowAppt?:
            | 'grooming'
            | 'dental'
            | 'daycare'
            | 'boarding'
            | 'transportation'
            | 'walking'
            | 'training'
            | 'evaluation'
            | 'memberships'
    ) => void;
    onRemove?: (petId: number | undefined, service: PetServiceWrapper | undefined) => void;
    onSaveDaycare: (daycare: Daycare, feedings: Array<Feeding>, medications: Array<Medication>, petId: number) => void;
    onSaveBoarding: (
        boarding: Boarding,
        feedings: Array<Feeding>,
        medications: Array<Medication>,
        petId: number
    ) => void;
    onSaveTransportation: (transportation: Transportation, petId: number) => void;
    onSaveWalking: (walking: Walking, petId: number) => void;
    draftSessionBookings?: Array<DataVaraible>;
    onSaveTraining: (training: Training, petId: number) => void;
    footerButtonText?: string;
    disableMembershipsAutoSelect?: boolean;
    hideServiceTypeTags?: boolean;
    initialMembershipSelection?: MembershipsSelection;
    initialMembershipServiceId?: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        petContainer: {
            marginTop: 8,
            marginBottom: 8
        },
        totalContainer: {
            height: '15%',
            borderTop: '1px solid #D4D4D4',
            marginTop: 11,
            marginBottom: 6
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            margin: '17px 27px 11px 27px',
            [theme.breakpoints.up('md')]: {
                margin: '20px 30px 13px 30px'
            }
        },
        content: {
            margin: '0px 15px 0px 15px',
            [theme.breakpoints.up('md')]: {
                margin: '0px 30px 0px 30px'
            }
        },
        addService: {
            cursor: 'pointer',
            color: '#92B4A7',
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '27px',
            paddingTop: 7
        }
    })
);

function toPetServiceWrapper(ps: PetService, customPrices?: Array<PetCustomPrice>): PetServiceWrapper {
    const resolved = resolveStandardPrice(ps.price, customPrices || [], ps.service.id);
    return {
        ...ps,
        duration: {
            hours: Math.trunc(ps.durationInMinutes / 60),
            minutes: ps.durationInMinutes % 60
        },
        beforePrice: ps.price,
        price: resolved.price,
        customPriceId: resolved.customPriceId,
        customPriceAmount: resolved.price
    };
}

export const PetAndServices: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const timezone = useTimeZone();

    const { hasDaycare, hasBoarding, hasGrooming } = useServiceCategories();

    const staffs = useSelector<RootState, Array<Staff>>(state => state.staff.staff);
    const transportationServices = useSelector<RootState, TransportationService[] | undefined>(
        state => state.services.transportationServices
    );
    const membershipsStatus = useSelector((state: RootState) => state.memberships.status);
    const membershipEnrollments = useSelector((state: RootState) => state.memberships.enrollments);
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const newBookingsStatus = useSelector((state: RootState) => state.newBookings.status);
    const petServices = useSelector((state: RootState) => state.newBookings.petServices);
    const servicesStatus = useSelector((state: RootState) => state.services.status);
    const daycareServices = useSelector((state: RootState) => state.services.daycareServices);
    const boardingServices = useSelector((state: RootState) => state.services.boardingServices);
    const walkingServices = useSelector((state: RootState) => state.services.walkingServices);
    const trainingServices = useSelector((state: RootState) => state.services.trainingServices);
    const customPrices = useSelector<RootState, Array<PetCustomPrice>>(state => state.pets.customPrices);
    const customPricesLoading = useSelector<RootState, boolean>(state => state.pets.customPricesLoading);

    const [service, setService] = useState<PetServiceWrapper | undefined>(props.service);
    const [staffId, setStaffId] = useState<number | undefined>(props.staffId ?? props.staffIdPreselected);
    const [date, setDate] = useState<Moment | undefined>(props.date);
    const [discount, setDiscount] = useState<Discount>(props.discount);
    const [petId, setPetId] = useState<number | undefined>(props.petId);
    const [defaultDiscount, setDefaultDiscount] = useState<string | undefined>(undefined);
    const [flow, setFlow] = useState<
        | 'grooming'
        | 'dental'
        | 'daycare'
        | 'boarding'
        | 'transportation'
        | 'walking'
        | 'training'
        | 'memberships'
        | 'evaluation'
        | undefined
    >(props.flow);
    const [showFeedingAndMedications, setShowFeedingAndMedications] = useState<boolean>(false);
    const [daycare, setDaycare] = useState<DaycareData | undefined>(undefined);
    const [boarding, setBoarding] = useState<BoardingData | undefined>(() => {
        if (props.flow !== 'boarding' || props.versionPetAndServices !== versionPetAndServices.edit || !props.service) {
            return undefined;
        }
        return buildEditBoardingData(props.service, props.date, props.dateEnd, props.roomUnitId);
    });
    const didUserChangeBoardingRoomRef = useRef(false);
    const [transportation, setTransportation] = useState<Transportation | undefined>(undefined);
    const [walking, setWalking] = useState<Walking | undefined>(undefined);
    const [training, setTraining] = useState<Training | undefined>(undefined);
    const [membershipApplyIntent, setMembershipApplyIntent] = useState<{
        selection: MembershipsSelection;
        serviceId: number;
        enrollmentId?: number;
        appointmentFrom: Moment;
    } | null>(null);
    const [membershipResolved, setMembershipResolved] = useState<{
        serviceId: number;
        resolvedFlow: MembershipDrivenServiceFlow;
        groomingPetService?: PetService;
        enrollmentId?: number;
        membershipItem?: MembershipItem;
        appointmentFrom: Moment;
    } | null>(null);
    const [membershipEnrollmentsReady, setMembershipEnrollmentsReady] = useState(true);
    const [membershipEnrollmentId, setMembershipEnrollmentId] = useState<number | undefined>(undefined);
    const [enrollmentAnchor, setEnrollmentAnchor] = useState<EnrollmentAnchor>(null);
    const [boardingMembershipAlertContext, setBoardingMembershipAlertContext] = useState<{
        membershipItem: MembershipItem;
        serviceId: number;
    } | null>(null);

    const appliedEnrollmentResolutionKeyRef = useRef<string | null>(null);
    const appliedInitialMembershipIntentRef = useRef(false);

    useEffect(() => {
        if (appliedInitialMembershipIntentRef.current) return;
        if (!props.initialMembershipSelection || props.initialMembershipServiceId == null) return;

        appliedInitialMembershipIntentRef.current = true;
        setMembershipApplyIntent({
            selection: props.initialMembershipSelection,
            serviceId: props.initialMembershipServiceId,
            enrollmentId: props.initialMembershipSelection.enrollmentId,
            appointmentFrom:
                props.initialMembershipSelection.appointmentFrom?.clone().tz(timezone, true) ?? moment().tz(timezone)
        });
    }, [props.initialMembershipSelection, props.initialMembershipServiceId, date, timezone]);

    useEffect(() => {
        if (service?.enrollment?.id == null || service.service?.id == null) {
            setEnrollmentAnchor(null);
            return;
        }

        const dateForAnchor =
            flow === 'boarding' ? boarding?.boarding?.from : flow === 'daycare' ? daycare?.daycare?.from : date;

        if (dateForAnchor?.isValid?.()) {
            setEnrollmentAnchor({
                dateKey: dateForAnchor.clone().format('YYYY-MM-DD'),
                serviceId: service.service.id
            });
        } else {
            setEnrollmentAnchor(null);
        }
    }, [service?.enrollment?.id, service?.service?.id, flow, date, boarding?.boarding?.from, daycare?.daycare?.from]);

    const showMembershipsTag = useMemo(() => {
        if (membershipsStatus === MembershipsStatus.SearchingEnrollments) return false;
        if (membershipsStatus === MembershipsStatus.SearchEnrollmentsError) return false;
        if (membershipsStatus !== MembershipsStatus.SearchEnrollmentsSuccess) return false;
        return membershipEnrollments.length > 0;
    }, [membershipsStatus, membershipEnrollments.length]);

    const membershipsTagLoading = useMemo(() => {
        return membershipsStatus === MembershipsStatus.SearchingEnrollments;
    }, [membershipsStatus]);

    const staffSlotTimes = useMemo(() => {
        const staff = staffs.find(s => s.id === staffId);
        if (!staff || !date) return [];

        const weekDay = date.format('dddd').toLowerCase();

        const customDay = staff.slots.customDays.find(cd => cd.on && cd.date.isSame(date, 'day'));
        const slots = customDay ? [customDay] : staff.slots.default.filter(s => s.weekDay === weekDay);

        return slots.map(slot => moment(slot.time, 'HH:mm').format('LT').toLowerCase());
    }, [staffId, date, staffs]);

    const getFlowActive = () => {
        if (props.flow) {
            return props.flow;
        }
        if (hasGrooming) {
            return 'grooming';
        }
        if (hasDaycare) {
            return 'daycare';
        }
        if (hasBoarding) {
            return 'boarding';
        }
        return 'grooming';
    };

    const getTitle = () => {
        if (props.isEvaluation) {
            return props.versionPetAndServices === versionPetAndServices.edit ? 'Edit Evaluation' : 'Pets';
        } else if (props.versionPetAndServices === versionPetAndServices.add) {
            return 'Add Service';
        } else if (props.versionPetAndServices === versionPetAndServices.edit) {
            return `Edit ${flow === 'daycare' ? 'Daycare' : 'Service'}`;
        } else {
            return 'Pet & Services';
        }
    };

    const isDisabledButton = () => {
        if (props.versionPetAndServices !== versionPetAndServices.edit) {
            if (!props.parentId || !petId || !service || !staffId || isNaN(Number(discount?.value))) {
                return true;
            }
            return false;
        } else {
            if (!petId || !service || !staffId) {
                return true;
            }
            return false;
        }
    };

    const handlerBook = () => {
        props.onBook(date, petId, service, staffId, discount, flow);
    };

    const handleRemoveAppointment = () => {
        props.onRemove && props.onRemove(props.petId, props.service);
    };

    const handlerBack = () => {
        setDate(props.date);
        // Don't reset service if it's an evaluation (keep the pre-loaded evaluation service)
        if (!props.isEvaluation) {
            setService(undefined);
        }
        setStaffId(undefined);
        props.onBack();
    };

    const handlerPet = (value: Pet) => {
        setPetId(value.id);
    };

    const handleChangeService = useCallback(
        (value: PetServiceWrapper, data: boolean) => {
            const serviceChanged = service?.service?.id !== value?.service?.id;
            const sameService = value?.service?.id === service?.service?.id;

            if (
                value?.businessArea === 'boarding' &&
                value.membershipItem != null &&
                value.service?.id != null &&
                value.selectedOffer == null
            ) {
                setBoardingMembershipAlertContext({
                    membershipItem: value.membershipItem,
                    serviceId: value.service.id
                });
            }

            setService(prev => {
                const next = { ...value } as PetServiceWrapper;
                const dealSelected = next.selectedOffer != null;
                const explicitMembershipClear =
                    Object.prototype.hasOwnProperty.call(value, 'membershipItem') &&
                    Object.prototype.hasOwnProperty.call(value, 'enrollment') &&
                    value.membershipItem === undefined &&
                    value.enrollment === undefined;
                if (data || sameService) {
                    if (!dealSelected) {
                        if (explicitMembershipClear) {
                            next.enrollment = undefined;
                            next.membershipItem = undefined;
                        } else {
                            if (next.enrollment?.id == null && prev?.enrollment?.id != null) {
                                next.enrollment = prev.enrollment;
                            }
                            if (next.membershipItem == null && prev?.membershipItem != null) {
                                next.membershipItem = prev.membershipItem;
                            }
                        }
                    } else {
                        next.enrollment = undefined;
                        next.membershipItem = undefined;
                    }
                    if (next.beforePrice == null && prev?.beforePrice != null) {
                        next.beforePrice = prev.beforePrice;
                    }
                }
                return next;
            });
            if (value?.businessArea) {
                const businessAreaFlow = value.businessArea as
                    | 'grooming'
                    | 'dental'
                    | 'daycare'
                    | 'boarding'
                    | 'transportation'
                    | 'walking'
                    | 'training'
                    | 'memberships';
                if (businessAreaFlow && businessAreaFlow !== flow) {
                    setFlow(businessAreaFlow);
                }
            }
            if (!data && serviceChanged) {
                setStaffId(undefined);
            }
            if (!data) {
                const onlyTogglingCustomPrice = sameService && value.saveCustomPrice !== service?.saveCustomPrice;
                if (!onlyTogglingCustomPrice) {
                    setDiscount(prev => ({
                        ...prev,
                        type: 'amount',
                        value: 0
                    }));
                    setDefaultDiscount('0.00');
                }
            }
        },
        [service?.service?.id, flow]
    );

    const handlerBoardingPeriodChange = useCallback((from: Moment, to: Moment) => {
        setBoarding(prev => {
            if (prev?.boarding == null) {
                return prev;
            }
            return {
                ...prev,
                boarding: {
                    ...prev.boarding,
                    from: from.clone(),
                    to: to.clone()
                }
            };
        });
    }, []);

    const handlerBoardingServiceChange = useCallback(
        (serviceWrapper: PetServiceWrapper) => {
            handleChangeService(serviceWrapper, true);
            const offer = serviceWrapper.selectedOffer;
            setBoarding(prev => {
                const prevBoarding = prev?.boarding;
                if (prevBoarding?.service?.serviceId == null) {
                    return prev;
                }
                if (offer != null) {
                    return {
                        feedings: prev?.feedings ?? [],
                        medications: prev?.medications ?? [],
                        boarding: {
                            ...prevBoarding,
                            enrollment: undefined,
                            price: offer.pricePerNight,
                            service: {
                                ...prevBoarding.service,
                                selectedOffer: offer,
                                price: offer.pricePerNight,
                                membershipItem: undefined,
                                enrollment: undefined
                            }
                        }
                    };
                }
                return {
                    feedings: prev?.feedings ?? [],
                    medications: prev?.medications ?? [],
                    boarding: {
                        ...prevBoarding,
                        enrollment: serviceWrapper.enrollment ?? prevBoarding.enrollment,
                        price: serviceWrapper.price,
                        service: {
                            ...prevBoarding.service,
                            selectedOffer: undefined,
                            price: serviceWrapper.price,
                            beforePrice: serviceWrapper.beforePrice,
                            membershipItem: serviceWrapper.membershipItem ?? prevBoarding.service?.membershipItem,
                            enrollment: serviceWrapper.enrollment ?? prevBoarding.service?.enrollment
                        }
                    }
                };
            });
        },
        [handleChangeService]
    );

    const handlerClearBoardingMembership = useCallback(() => {
        setBoardingMembershipAlertContext(null);
        setMembershipEnrollmentId(undefined);
        setEnrollmentAnchor(null);
        setMembershipResolved(null);
        setService(prev => {
            if (prev == null) {
                return prev;
            }
            return {
                ...prev,
                membershipItem: undefined,
                enrollment: undefined
            };
        });
        setBoarding(prev => {
            if (prev?.boarding?.service == null) {
                return prev;
            }
            return {
                ...prev,
                boarding: {
                    ...prev.boarding,
                    enrollment: undefined,
                    service: {
                        ...prev.boarding.service,
                        membershipItem: undefined,
                        enrollment: undefined
                    }
                }
            };
        });
    }, []);

    const handlerBoardingRoomChanged = useCallback(() => {
        didUserChangeBoardingRoomRef.current = true;
    }, []);

    const handlerBoardingNext = useCallback(
        (boardingModel: Boarding) => {
            setBoarding(prev => ({
                feedings: prev?.feedings ?? [],
                medications: prev?.medications ?? [],
                boarding: boardingModel
            }));
            if (boardingModel.service?.serviceId == null) {
                return;
            }
            const selectedOffer = boardingModel.service.selectedOffer;
            handleChangeService(
                {
                    service: {
                        id: boardingModel.service.serviceId,
                        name: boardingModel.service.serviceName ?? 'Boarding Service'
                    },
                    price: boardingModel.price,
                    beforePrice: boardingModel.service.beforePrice ?? boardingModel.service.price,
                    durationInMinutes: boardingModel.service.duration ?? 0,
                    duration: boardingModel.service.durationService ?? {
                        hours: Math.trunc((boardingModel.service.duration ?? 0) / 60),
                        minutes: (boardingModel.service.duration ?? 0) % 60
                    },
                    businessArea: 'boarding',
                    color: boardingModel.service.serviceColor ?? '',
                    room: boardingModel.room,
                    roomUnitId: boardingModel.roomUnitId,
                    notes: boardingModel.notes,
                    ...(selectedOffer != null
                        ? { selectedOffer, enrollment: undefined, membershipItem: undefined }
                        : {
                              enrollment: boardingModel.enrollment ?? service?.enrollment,
                              membershipItem: boardingModel.service.membershipItem ?? service?.membershipItem
                          })
                },
                true
            );
        },
        [handleChangeService, service?.enrollment, service?.membershipItem]
    );

    const syncDaycareToService = useCallback(
        (daycareModel: Daycare, selectedDates?: Array<Moment>) => {
            setDaycare(prev => ({
                feedings: prev?.feedings ?? [],
                medications: prev?.medications ?? [],
                daycare: {
                    ...daycareModel,
                    ...(selectedDates != null ? { selectedDates } : {})
                }
            }));
            if (daycareModel.service?.serviceId == null) {
                return;
            }
            const durationMinutes = (() => {
                if (daycareModel.from?.isValid() && daycareModel.to?.isValid()) {
                    const diff = daycareModel.to.diff(daycareModel.from, 'minutes');
                    if (diff > 0) {
                        return diff;
                    }
                }
                if (daycareModel.from?.isValid()) {
                    const closing = moment(getTodayClosingTime(daycareModel.from, marketplace)).tz(timezone);
                    const closingDiff = closing.diff(daycareModel.from, 'minutes');
                    if (closingDiff > 0) {
                        return closingDiff;
                    }
                }
                return daycareModel.service.duration ?? 60;
            })();
            handleChangeService(
                {
                    service: {
                        id: daycareModel.service.serviceId,
                        name: daycareModel.service.serviceName ?? 'Daycare Service'
                    },
                    price: daycareModel.price,
                    beforePrice: daycareModel.service.beforePrice ?? service?.beforePrice ?? daycareModel.service.price,
                    durationInMinutes: durationMinutes,
                    duration: daycareModel.service.durationService ?? {
                        hours: Math.trunc(durationMinutes / 60),
                        minutes: durationMinutes % 60
                    },
                    businessArea: 'daycare',
                    color: daycareModel.service.serviceColor ?? '',
                    room: daycareModel.room,
                    roomUnitId: daycareModel.room?.units?.[0]?.id ?? daycareModel.roomUnitId,
                    discount: daycareModel.discount,
                    variableDuration: daycareModel.service.variableDuration,
                    latePickupFee: daycareModel.service.latePickupFee,
                    enrollment: daycareModel.enrollment ?? service?.enrollment,
                    membershipItem: daycareModel.membershipItem ?? service?.membershipItem
                },
                true
            );
        },
        [handleChangeService, marketplace, service?.enrollment, service?.membershipItem, timezone]
    );

    const handlerDaycareNext = useCallback(
        (daycareModel: Daycare) => {
            syncDaycareToService(daycareModel, daycareModel.selectedDates);
        },
        [syncDaycareToService]
    );

    const handlerDaycareChange = useCallback(
        (daycareModel: Daycare, selectedDates: Array<Moment>) => {
            syncDaycareToService(daycareModel, selectedDates);
        },
        [syncDaycareToService]
    );

    const handleMembershipsServiceSelect = useCallback(
        (selection: MembershipsSelection) => {
            if (!petId) return;
            const serviceId = parseMembershipServiceId(selection.item);
            if (serviceId == null) return;
            const appointmentFrom = selection.appointmentFrom?.clone().tz(timezone, true) ?? moment().tz(timezone);
            if (appointmentFrom.isValid()) {
                setDate(appointmentFrom);
            }
            if (flowFromMembershipItemType(selection.item.itemType) === 'boarding') {
                setBoardingMembershipAlertContext({ membershipItem: selection.item, serviceId });
            }
            const eId = selection.enrollmentId;
            if (eId != null) {
                setMembershipEnrollmentId(eId);
            }
            setMembershipEnrollmentsReady(true);
            setMembershipApplyIntent({
                selection,
                serviceId,
                enrollmentId: eId,
                appointmentFrom
            });
            dispatch(fetchPetServicesThunk(apiClientWrapper, petId));
            dispatch(fetchPetCustomPricesThunk(apiClientWrapper, petId));
        },
        [petId, date, timezone, dispatch, apiClientWrapper]
    );

    useEffect(() => {
        if (petId) {
            dispatch(fetchPetCustomPricesThunk(apiClientWrapper, petId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId]);

    useEffect(() => {
        if (membershipApplyIntent && newBookingsStatus === NewBookingsStatus.Error) {
            setMembershipApplyIntent(null);
            setMembershipEnrollmentsReady(true);
        }
    }, [membershipApplyIntent, newBookingsStatus]);

    useEffect(() => {
        if (membershipResolved && !membershipResolved.groomingPetService && servicesStatus === ServicesStatus.Error) {
            setMembershipResolved(null);
            setMembershipEnrollmentsReady(true);
        }
    }, [membershipResolved, servicesStatus]);

    useEffect(() => {
        if (!membershipApplyIntent || !petId) return;
        if (newBookingsStatus !== NewBookingsStatus.FetchPetServicesSuccess) return;

        const { selection, serviceId, enrollmentId: intentEnrollmentId, appointmentFrom } = membershipApplyIntent;

        let petServiceFromLookup: PetService | undefined;
        let targetFlow: MembershipDrivenServiceFlow | undefined = flowFromMembershipItemType(selection.item.itemType);

        if (membershipItemNeedsPetServicesLookup(selection.item.itemType)) {
            petServiceFromLookup = petServices.find(s => s.service.id === serviceId);
            if (!petServiceFromLookup) {
                setMembershipApplyIntent(null);
                setMembershipEnrollmentsReady(true);
                return;
            }
            targetFlow = flowFromPetServiceBusinessArea(petServiceFromLookup.businessArea);
        }

        if (!targetFlow) {
            setMembershipApplyIntent(null);
            setMembershipEnrollmentsReady(true);
            return;
        }

        setFlow(targetFlow);

        if (targetFlow === 'grooming' || targetFlow === 'dental') {
            let ps = petServiceFromLookup;
            if (!ps) {
                ps = petServices.find(s => s.service.id === serviceId);
            }
            if (!ps) {
                setMembershipApplyIntent(null);
                setMembershipEnrollmentsReady(true);
                return;
            }
            const refined = flowFromPetServiceBusinessArea(ps.businessArea);
            if (refined !== targetFlow) {
                setFlow(refined);
            }
            setMembershipResolved({
                serviceId,
                resolvedFlow: refined,
                groomingPetService: ps,
                enrollmentId: intentEnrollmentId,
                membershipItem: selection.item,
                appointmentFrom
            });
            setMembershipApplyIntent(null);
            return;
        }

        setMembershipResolved({
            serviceId,
            resolvedFlow: targetFlow,
            enrollmentId: intentEnrollmentId,
            membershipItem: selection.item,
            appointmentFrom
        });
        setMembershipApplyIntent(null);

        switch (targetFlow) {
            case 'daycare':
                dispatch(fetchDaycareServicesThunk(petId, apiClientWrapper.marketplaceId));
                break;
            case 'boarding': {
                const stayFrom = appointmentFrom.clone().tz(timezone);
                const stayTo = stayFrom.clone().add(1, 'day');
                if (petId && stayFrom.isValid() && stayTo.isValid()) {
                    dispatch(
                        fetchBoardingOffersThunk(
                            petId,
                            stayFrom.format('YYYY-MM-DD'),
                            stayTo.format('YYYY-MM-DD'),
                            apiClientWrapper.marketplaceId
                        )
                    );
                }
                break;
            }
            case 'walking':
                dispatch(fetchWalkingServicesThunk(petId, apiClientWrapper.marketplaceId));
                break;
            case 'training':
                dispatch(fetchTrainingServicesThunk(petId, apiClientWrapper.marketplaceId));
                break;
            case 'transportation':
                dispatch(fetchTransportationServicesThunk(petId, apiClientWrapper.marketplaceId));
                break;
            default:
                break;
        }
    }, [
        membershipApplyIntent,
        newBookingsStatus,
        petServices,
        petId,
        date,
        dispatch,
        timezone,
        apiClientWrapper.marketplaceId
    ]);

    useEffect(() => {
        if (!membershipResolved) {
            if (!membershipResolved) {
                appliedEnrollmentResolutionKeyRef.current = null;
            }
            return;
        }

        const resolutionKey = `${membershipResolved.serviceId}:${membershipResolved.resolvedFlow}:${
            membershipResolved.enrollmentId ?? 'x'
        }:${membershipResolved.appointmentFrom.format('YYYY-MM-DD')}`;
        if (appliedEnrollmentResolutionKeyRef.current === resolutionKey) {
            return;
        }

        const {
            resolvedFlow,
            serviceId,
            groomingPetService,
            enrollmentId: resolvedEnrollmentId,
            membershipItem,
            appointmentFrom
        } = membershipResolved;

        const hasEnrollment = resolvedEnrollmentId != null;
        if (hasEnrollment && !membershipEnrollmentsReady) return;
        if (customPricesLoading) return;

        const enrollmentIdToApply = hasEnrollment
            ? membershipEnrollmentId ?? service?.enrollment?.id ?? resolvedEnrollmentId
            : undefined;
        const enrollmentToApply =
            enrollmentIdToApply == null
                ? undefined
                : membershipEnrollments.find(e => e.id === enrollmentIdToApply) ??
                  (service?.enrollment?.id === enrollmentIdToApply ? service.enrollment : undefined);

        const clearResolved = () => {
            appliedEnrollmentResolutionKeyRef.current = resolutionKey;
            setMembershipResolved(null);
            setMembershipEnrollmentsReady(false);
        };

        if (groomingPetService) {
            const { wrapper, previewDiscount } = applyMembershipCatalogPriceWithPreviewDiscount(
                toPetServiceWrapper(groomingPetService, customPrices),
                enrollmentToApply,
                membershipItem,
                service
            );
            handleChangeService(wrapper, true);
            if (previewDiscount) {
                setDiscount(previewDiscount);
                setDefaultDiscount(String(previewDiscount.value));
            }
            clearResolved();
            return;
        }

        switch (resolvedFlow) {
            case 'daycare': {
                if (servicesStatus !== ServicesStatus.FetchDaycareServicesSuccess) return;
                const raw = daycareServices?.find(s => s.serviceId === serviceId);
                if (!raw) return;
                const wrapper: DaycareServiceWrapper = {
                    ...raw,
                    durationService: {
                        hours: Math.trunc(raw.duration / 60),
                        minutes: raw.duration % 60
                    },
                    beforePrice: raw.price,
                    serviceColor: raw.serviceColor,
                    latePickupFee: raw.latePickupFee,
                    dropOffTime: raw.dropOffTime,
                    pickUpTime: raw.pickUpTime,
                    variableDuration: raw.variableDuration
                };
                const from = appointmentFrom.clone().tz(timezone);
                const to = moment(getTodayClosingTime(from, marketplace)).tz(marketplace.schedule.timeZone);
                const minutes = Math.max(wrapper.duration, 1);
                const catalogPrice = raw.price;
                const resolvedDaycarePrice = resolveStandardPrice(catalogPrice, customPrices || [], serviceId);
                const daycarePreviewDiscount = computeMembershipPreviewDiscount(
                    resolvedDaycarePrice.price,
                    membershipItem
                );
                const selectedDay = from.clone().tz(timezone).startOf('day');
                setDaycare(prev => ({
                    feedings: prev?.feedings ?? [],
                    medications: prev?.medications ?? [],
                    daycare: {
                        from,
                        to,
                        service: { ...wrapper, beforePrice: catalogPrice },
                        room: prev?.daycare?.room,
                        roomUnitId: prev?.daycare?.roomUnitId,
                        price: resolvedDaycarePrice.price,
                        priceForHour: resolvedDaycarePrice.price / (minutes / 60),
                        discount: resolveMembershipServiceDiscount(daycarePreviewDiscount, prev?.daycare?.discount),
                        selectedDates: [selectedDay],
                        customPriceId: resolvedDaycarePrice.customPriceId
                    }
                }));
                handleChangeService(
                    applyMembershipDaycarePricing(
                        {
                            service: { id: raw.serviceId, name: raw.serviceName },
                            price: resolvedDaycarePrice.price,
                            durationInMinutes: raw.duration,
                            businessArea: 'daycare',
                            color: raw.serviceColor || '',
                            duration: {
                                hours: Math.trunc(raw.duration / 60),
                                minutes: raw.duration % 60
                            },
                            beforePrice: catalogPrice,
                            variableDuration: raw.variableDuration,
                            customPriceId: resolvedDaycarePrice.customPriceId,
                            customPriceAmount: resolvedDaycarePrice.price
                        },
                        enrollmentToApply,
                        membershipItem,
                        service
                    ),
                    true
                );
                clearResolved();
                break;
            }
            case 'boarding': {
                if (servicesStatus !== ServicesStatus.FetchBoardingOffersSuccess) return;
                const raw = boardingServices?.find(s => s.serviceId === serviceId);
                if (!raw) return;
                const from = appointmentFrom.clone().tz(timezone);
                const to = from.clone().add(1, 'day');
                const catalogPrice = getBoardingCatalogNightlyPrice(raw);
                const standardRatePrice = getBoardingStandardNightlyPrice(raw);
                const resolvedBoardingPrice = resolveStandardPrice(catalogPrice, customPrices || [], serviceId);
                const boardingMembershipPricing =
                    membershipItem != null
                        ? computeMembershipDisplayPrice(resolvedBoardingPrice.price, membershipItem)
                        : null;
                const nightlyPrice =
                    enrollmentToApply != null && membershipItem != null
                        ? boardingMembershipPricing!.displayPrice
                        : resolvedBoardingPrice.customPriceId != null
                        ? resolvedBoardingPrice.price
                        : standardRatePrice;
                const wrapper: BoardingServiceWrapper = {
                    ...raw,
                    durationService: {
                        hours: Math.trunc(raw.duration / 60),
                        minutes: raw.duration % 60
                    },
                    catalogPrice: raw.catalogPrice ?? catalogPrice,
                    beforePrice: resolvedBoardingPrice.price,
                    price: nightlyPrice,
                    serviceColor: raw.serviceColor,
                    latePickupFee: raw.latePickupFee,
                    checkInDateTime: raw.checkInTime?.start ? addTimeToDate(from, raw.checkInTime.start) : undefined,
                    checkOutDateTime: raw.checkOutTime?.start ? addTimeToDate(to, raw.checkOutTime.start) : undefined,
                    ...(membershipItem != null ? { membershipItem } : {}),
                    ...(enrollmentToApply != null ? { enrollment: enrollmentToApply } : {})
                };
                if (membershipItem != null) {
                    setBoardingMembershipAlertContext({ membershipItem, serviceId });
                }
                if (from.isValid()) {
                    setDate(from);
                }
                setBoarding(prev => ({
                    feedings: prev?.feedings ?? [],
                    medications: prev?.medications ?? [],
                    boarding: {
                        from,
                        to,
                        service: wrapper,
                        room: raw.rooms?.[0] ?? prev?.boarding?.room,
                        roomUnitId: undefined,
                        price: nightlyPrice,
                        notes: prev?.boarding?.notes,
                        customPriceId: resolvedBoardingPrice.customPriceId
                    }
                }));
                handleChangeService(
                    applyMembershipBoardingPricing(
                        {
                            service: { id: raw.serviceId, name: raw.serviceName },
                            price: resolvedBoardingPrice.price,
                            durationInMinutes: raw.duration,
                            businessArea: 'boarding',
                            color: raw.serviceColor || '',
                            duration: {
                                hours: Math.trunc(raw.duration / 60),
                                minutes: raw.duration % 60
                            },
                            beforePrice: catalogPrice,
                            customPriceId: resolvedBoardingPrice.customPriceId,
                            customPriceAmount: resolvedBoardingPrice.price
                        },
                        enrollmentToApply,
                        membershipItem,
                        service
                    ),
                    true
                );
                clearResolved();
                break;
            }
            case 'walking': {
                if (servicesStatus !== ServicesStatus.FetchWalkingServicesSuccess) return;
                const ws = walkingServices?.find(s => s.serviceId === serviceId);
                if (!ws) return;
                const from = appointmentFrom.clone().tz(timezone);
                const to = from.clone().add(Math.max(ws.duration || 60, 15), 'minutes');
                const resolvedWalkingPrice = resolveStandardPrice(ws.price, customPrices || [], serviceId);
                const walkingPreviewDiscount = computeMembershipPreviewDiscount(
                    resolvedWalkingPrice.price,
                    membershipItem
                );
                setWalking(prev => ({
                    from,
                    to,
                    service: ws,
                    price: resolvedWalkingPrice.price,
                    customPriceId: resolvedWalkingPrice.customPriceId,
                    discount: resolveMembershipServiceDiscount(walkingPreviewDiscount, prev?.discount),
                    staffId: prev?.staffId,
                    address: prev?.address ?? '',
                    selectedDates: prev?.selectedDates?.length
                        ? prev.selectedDates
                        : [from.clone().tz(timezone).startOf('day')]
                }));
                handleChangeService(
                    applyMembershipCatalogPriceWithPreviewDiscount(
                        {
                            service: { id: ws.serviceId, name: ws.serviceName },
                            price: resolvedWalkingPrice.price,
                            durationInMinutes: ws.duration,
                            businessArea: 'walking',
                            color: ws.color,
                            beforePrice: ws.price,
                            customPriceId: resolvedWalkingPrice.customPriceId,
                            customPriceAmount: resolvedWalkingPrice.price,
                            duration: {
                                hours: Math.trunc(ws.duration / 60),
                                minutes: ws.duration % 60
                            },
                            inHomeService: ws.inHomeService
                        },
                        enrollmentToApply,
                        membershipItem,
                        service
                    ).wrapper,
                    true
                );
                clearResolved();
                break;
            }
            case 'training': {
                if (servicesStatus !== ServicesStatus.FetchTrainingServicesSuccess) return;
                const ts = trainingServices?.find(s => s.serviceId === serviceId);
                if (!ts) return;
                const from = appointmentFrom.clone().tz(timezone);
                const to = from.clone().add(Math.max(ts.duration || 60, 15), 'minutes');
                const resolvedTrainingPrice = resolveStandardPrice(ts.price, customPrices || [], serviceId);
                const trainingPreviewDiscount = computeMembershipPreviewDiscount(
                    resolvedTrainingPrice.price,
                    membershipItem
                );
                setTraining(prev => ({
                    from,
                    to,
                    service: ts,
                    price: resolvedTrainingPrice.price,
                    customPriceId: resolvedTrainingPrice.customPriceId,
                    discount: resolveMembershipServiceDiscount(trainingPreviewDiscount, prev?.discount),
                    staffId: prev?.staffId,
                    address: prev?.address ?? '',
                    selectedDates: prev?.selectedDates?.length
                        ? prev.selectedDates
                        : [from.clone().tz(timezone).startOf('day')]
                }));
                handleChangeService(
                    applyMembershipCatalogPriceWithPreviewDiscount(
                        {
                            service: { id: ts.serviceId, name: ts.serviceName },
                            price: resolvedTrainingPrice.price,
                            durationInMinutes: ts.duration,
                            businessArea: 'training',
                            color: ts.color,
                            beforePrice: ts.price,
                            customPriceId: resolvedTrainingPrice.customPriceId,
                            customPriceAmount: resolvedTrainingPrice.price,
                            duration: {
                                hours: Math.trunc(ts.duration / 60),
                                minutes: ts.duration % 60
                            },
                            bookingType: ts.bookingType,
                            maxPets: ts.maxPets,
                            inHomeService: ts.inHomeService
                        },
                        enrollmentToApply,
                        membershipItem,
                        service
                    ).wrapper,
                    true
                );
                clearResolved();
                break;
            }
            case 'transportation': {
                if (servicesStatus !== ServicesStatus.FetchTransportationServicesSuccess) return;
                const tr = transportationServices?.find(s => s.serviceId === serviceId);
                if (!tr) return;
                const openingTime = getTodayOpeningTime(appointmentFrom, marketplace);
                const pickupTime = {
                    from: openingTime.clone(),
                    to: openingTime.clone().add(1, 'hour')
                };
                const dropOffTime = {
                    from: openingTime.clone().add(3, 'hours'),
                    to: openingTime.clone().add(4, 'hours')
                };
                const pickupResolved = resolvePickupPrice(tr.pickupPrice, customPrices || [], serviceId);
                const dropoffResolved = resolveDropoffPrice(tr.dropoffPrice, customPrices || [], serviceId);
                const pickupPreviewDiscount = computeMembershipPreviewDiscount(pickupResolved.price, membershipItem);
                const dropOffPreviewDiscount = computeMembershipPreviewDiscount(dropoffResolved.price, membershipItem);
                setTransportation(prev => ({
                    transportType: prev?.transportType ?? 'pickup',
                    service: tr,
                    selectedStaff: prev?.selectedStaff,
                    vehicle: prev?.vehicle,
                    enrollment: enrollmentToApply,
                    membershipItem,
                    selectedDates: prev?.selectedDates?.length
                        ? prev.selectedDates
                        : [appointmentFrom.clone().tz(timezone).startOf('day')],
                    pickup: {
                        price: pickupResolved.price,
                        customPriceId: pickupResolved.customPriceId,
                        discount: pickupPreviewDiscount ?? prev?.pickup?.discount ?? undefined,
                        time: prev?.pickup?.time ?? pickupTime,
                        address: prev?.pickup?.address ?? '',
                        instructions: prev?.pickup?.instructions ?? ''
                    },
                    dropOff: {
                        price: dropoffResolved.price,
                        customPriceId: dropoffResolved.customPriceId,
                        discount: dropOffPreviewDiscount ?? prev?.dropOff?.discount ?? undefined,
                        time: prev?.dropOff?.time ?? dropOffTime,
                        address: prev?.dropOff?.address ?? '',
                        instructions: prev?.dropOff?.instructions ?? ''
                    }
                }));
                handleChangeService(
                    applyMembershipCatalogPriceWithPreviewDiscount(
                        {
                            service: { id: tr.serviceId, name: tr.serviceName },
                            price: pickupResolved.price,
                            durationInMinutes: tr.duration,
                            businessArea: 'transportation',
                            color: tr.color,
                            beforePrice: tr.pickupPrice,
                            customPriceId: pickupResolved.customPriceId,
                            customPriceAmount: pickupResolved.price,
                            duration: {
                                hours: Math.trunc(tr.duration / 60),
                                minutes: tr.duration % 60
                            },
                            serviceItems: []
                        },
                        enrollmentToApply,
                        membershipItem,
                        service
                    ).wrapper,
                    true
                );
                clearResolved();
                break;
            }
            default:
                setMembershipResolved(null);
                break;
        }
    }, [
        membershipResolved,
        servicesStatus,
        daycareServices,
        boardingServices,
        walkingServices,
        trainingServices,
        transportationServices,
        date,
        timezone,
        marketplace,
        handleChangeService,
        membershipEnrollmentsReady,
        membershipEnrollmentId,
        service,
        customPrices,
        customPricesLoading
    ]);

    const handlerStaff = (staffSelected?: Staff) => {
        setStaffId(staffSelected?.id);
    };

    const handlerChangeDiscount = (value: Discount) => {
        setDiscount(prev => ({
            ...prev,
            type: value?.type,
            value: value?.value
        }));
    };

    useEffect(() => {
        if (petId !== props.petId) {
            setDate(props.date);
            // Don't reset service if it's an evaluation (keep the pre-loaded evaluation service)
            if (!props.isEvaluation) {
                setService(undefined);
            }
            setStaffId(undefined);
            setDiscount(prev => ({
                ...prev,
                type: 'amount',
                value: 0
            }));
            setDefaultDiscount('0.00');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId]);

    const serviceFlowLayoutSyncKey = useMemo(() => {
        const s = service ?? props.service;
        if (!s) {
            return [
                'nosvc',
                flow ?? props.flow ?? '',
                props.date?.valueOf() ?? '',
                props.dateEnd?.valueOf() ?? '',
                props.staffId ?? props.staffIdPreselected ?? '',
                props.transportationType ?? '',
                props.roomUnitId ?? '',
                props.discount?.type ?? '',
                props.discount?.value ?? ''
            ].join('|');
        }
        return [
            s.service?.id,
            s.service?.name,
            s.price,
            s.durationInMinutes,
            s.duration?.hours,
            s.duration?.minutes,
            s.roomUnitId,
            s.enrollment?.id,
            s.selectedOffer?.uuid,
            s.selectedOffer?.id,
            s.membershipItem?.id,
            s.beforePrice,
            s.notes,
            s.address,
            s.inHomeService,
            s.bookingType,
            s.color,
            s.maxPets,
            s.variableDuration,
            s.latePickupFee,
            s.discount?.type,
            s.discount?.value,
            props.discount?.type,
            props.discount?.value,
            props.date?.valueOf() ?? '',
            props.dateEnd?.valueOf() ?? '',
            props.staffId ?? props.staffIdPreselected ?? '',
            flow ?? props.flow,
            props.transportationType ?? '',
            props.roomUnitId ?? '',
            s.feedings?.length ?? 0,
            s.medications?.length ?? 0,
            s.serviceItems?.length ?? 0,
            s.vehicle?.id,
            s.room
        ].join('|');
    }, [
        service,
        props.service,
        props.discount,
        props.date,
        props.dateEnd,
        props.staffId,
        props.staffIdPreselected,
        flow,
        props.flow,
        props.transportationType,
        props.roomUnitId
    ]);

    useEffect(() => {
        const activeFlow = flow ?? props.flow;
        const daycareService = service ?? props.service;
        if (daycareService && activeFlow === 'daycare') {
            const resolvedDaycareRoomUnitId =
                props.roomUnitId ?? daycareService.roomUnitId ?? daycare?.daycare?.roomUnitId;
            setDaycare(prev => {
                const defaultFrom = props.date || moment().tz(timezone);
                const defaultTo = props.dateEnd?.isValid()
                    ? props.dateEnd.clone().tz(timezone)
                    : moment(getTodayClosingTime(defaultFrom, marketplace)).tz(timezone);
                const fromMoment = prev?.daycare?.from?.isValid() ? prev.daycare.from : defaultFrom;
                const toMoment = prev?.daycare?.to?.isValid() ? prev.daycare.to : defaultTo;
                const resolvedPrice = prev?.daycare?.price ?? daycareService.price;
                const durationMinutes = daycareService.durationInMinutes || 60;
                const resolvedDiscount =
                    prev?.daycare?.discount ??
                    ({
                        type: (daycareService?.discount?.type === 'none' || props.discount?.type === 'none'
                            ? 'amount'
                            : daycareService?.discount?.type || props.discount?.type) as 'amount' | 'percentage',
                        value:
                            daycareService?.discount?.type === 'none' || props.discount?.type === 'none'
                                ? 0
                                : (daycareService?.discount?.value || props.discount?.value) ?? 0
                    } as ValueTypeSelectorValue);
                return {
                    feedings: prev?.feedings ?? daycareService.feedings ?? [],
                    medications: prev?.medications ?? daycareService.medications ?? [],
                    daycare: {
                        from: fromMoment,
                        to: toMoment,
                        service: {
                            serviceId: daycareService.service.id,
                            serviceName: daycareService.service.name,
                            price: resolvedPrice,
                            duration: durationMinutes,
                            durationService: daycareService.duration,
                            beforePrice: daycareService.beforePrice ?? daycareService.price,
                            variableDuration: daycareService.variableDuration,
                            latePickupFee: daycareService.latePickupFee
                        } as DaycareServiceWrapper,
                        room: prev?.daycare?.room ?? daycareService.room,
                        roomUnitId: resolvedDaycareRoomUnitId,
                        price: resolvedPrice,
                        priceForHour: resolvedPrice / (durationMinutes / 60),
                        discount: resolvedDiscount,
                        selectedDates:
                            props.versionPetAndServices === versionPetAndServices.edit
                                ? [fromMoment.clone().tz(marketplace.schedule.timeZone).startOf('day')]
                                : prev?.daycare?.selectedDates?.length
                                ? prev.daycare.selectedDates
                                : [fromMoment.clone().tz(marketplace.schedule.timeZone).startOf('day')],
                        enrollment: daycareService.enrollment ?? prev?.daycare?.enrollment,
                        membershipItem: daycareService.membershipItem ?? prev?.daycare?.membershipItem,
                        saveCustomPrice: prev?.daycare?.saveCustomPrice,
                        customPriceId: prev?.daycare?.customPriceId
                    }
                };
            });
        }
        if (props.flow === 'boarding') {
            const boardingService = service ?? props.service;
            if (!boardingService) {
                return;
            }

            const resolvedBoardingRoomUnitId = didUserChangeBoardingRoomRef.current
                ? boardingService.roomUnitId ?? boarding?.boarding?.roomUnitId
                : props.roomUnitId ?? boardingService.roomUnitId ?? boarding?.boarding?.roomUnitId;
            const isBoardingEdit = props.versionPetAndServices === versionPetAndServices.edit;
            const catalogBoardingService = boardingServices?.find(s => s.serviceId === boardingService.service.id);
            const catalogNightlyPrice =
                catalogBoardingService != null
                    ? getBoardingCatalogNightlyPrice(catalogBoardingService)
                    : boardingService.beforePrice ?? boardingService.price;
            const overlayBoardingPrice = resolveStandardPrice(
                catalogNightlyPrice,
                customPrices || [],
                boardingService.service.id
            );
            const membershipItemForPricing =
                boardingService.membershipItem ?? boarding?.boarding?.service?.membershipItem;
            const catalogResolvedNightlyPrice =
                membershipItemForPricing != null && catalogBoardingService != null
                    ? resolveBoardingNightlyPriceFromService(
                          {
                              ...catalogBoardingService,
                              selectedOffer: boardingService.selectedOffer
                          },
                          membershipItemForPricing,
                          overlayBoardingPrice.price
                      )
                    : overlayBoardingPrice.price;
            setBoarding(prev => {
                const defaultFrom = props.date || moment().tz(timezone);
                const defaultTo = props.dateEnd || defaultFrom.clone().add(1, 'day');
                const fromMoment = prev?.boarding?.from?.isValid() === true ? prev.boarding.from : defaultFrom;
                const toMoment = prev?.boarding?.to?.isValid() === true ? prev.boarding.to : defaultTo;
                const bookedNightly = prev?.boarding?.price ?? boardingService.price;
                return {
                    feedings: prev?.feedings ?? boardingService.feedings ?? [],
                    medications: prev?.medications ?? boardingService.medications ?? [],
                    boarding: {
                        from: fromMoment,
                        to: toMoment,
                        enrollment: boardingService.enrollment ?? prev?.boarding?.enrollment,
                        service: {
                            serviceId: boardingService.service.id,
                            serviceName: boardingService.service.name,
                            price: boardingService.price,
                            duration: boardingService.durationInMinutes || 0,
                            durationService: boardingService.duration,
                            beforePrice: isBoardingEdit
                                ? boardingService.beforePrice ?? overlayBoardingPrice.price
                                : overlayBoardingPrice.price,
                            catalogPrice: catalogBoardingService?.catalogPrice ?? catalogNightlyPrice,
                            selectedOffer: boardingService.selectedOffer,
                            ...(catalogBoardingService?.offers != null
                                ? { offers: catalogBoardingService.offers }
                                : {}),
                            ...(boardingService.membershipItem != null
                                ? { membershipItem: boardingService.membershipItem }
                                : {}),
                            ...(boardingService.enrollment != null ? { enrollment: boardingService.enrollment } : {})
                        } as BoardingServiceWrapper,
                        room: boardingService.room ?? prev?.boarding?.room,
                        roomUnitId: resolvedBoardingRoomUnitId,
                        price: isBoardingEdit ? bookedNightly : catalogResolvedNightlyPrice,
                        notes: boardingService.notes ?? prev?.boarding?.notes,
                        customPriceId: isBoardingEdit
                            ? prev?.boarding?.customPriceId
                            : overlayBoardingPrice.customPriceId
                    }
                };
            });
        }
        if (props.service && props.flow === 'transportation') {
            const isPickup = props.transportationType === 'pickup';
            const resolvedStaffId = props.staffId ?? props.staffIdPreselected;
            const fullTransportationService = transportationServices?.find(
                (s: TransportationService) => s.serviceId === props.service?.service.id
            );

            const pickupPrice =
                (props.transportationType === 'pickup' ? props.service.price : undefined) ??
                fullTransportationService?.pickupPrice ??
                props.service.price ??
                0;
            const dropoffPrice =
                (props.transportationType === 'dropoff' ? props.service.price : undefined) ??
                fullTransportationService?.dropoffPrice ??
                props.service.price ??
                0;

            const transportationFormatted: Transportation = {
                transportType: isPickup ? 'pickup' : 'dropoff',
                service: {
                    serviceId: props.service.service.id,
                    serviceName: props.service.service.name,
                    duration: props.service.durationInMinutes || 0,
                    color: props.service.color,
                    serviceItems: props.service.serviceItems,
                    pickupPrice: pickupPrice,
                    dropoffPrice: dropoffPrice
                } as TransportationService,
                selectedStaff: staffs?.find((staff: Staff) => staff.id === resolvedStaffId),
                vehicle: props.service.vehicle,
                pickup: isPickup
                    ? ({
                          time: {
                              from: props.date || moment().tz(timezone),
                              to: props.dateEnd || moment().tz(timezone)
                          },
                          price: pickupPrice,
                          discount:
                              props.service.discount?.type && props.service.discount.type !== 'none'
                                  ? {
                                        type: props.service.discount.type as 'amount' | 'percentage',
                                        value: props.service.discount.value || 0
                                    }
                                  : props.discount?.type && props.discount.type !== 'none'
                                  ? {
                                        type: props.discount.type as 'amount' | 'percentage',
                                        value: props.discount.value || 0
                                    }
                                  : undefined,
                          address: props.service.address || '',
                          instructions: props.service.instructions || ''
                      } as TransportationData)
                    : undefined,
                dropOff: !isPickup
                    ? ({
                          time: {
                              from: props.date || moment().tz(timezone),
                              to: props.dateEnd || moment().tz(timezone)
                          },
                          price: dropoffPrice,
                          discount:
                              props.service.discount?.type && props.service.discount.type !== 'none'
                                  ? {
                                        type: props.service.discount.type as 'amount' | 'percentage',
                                        value: props.service.discount.value || 0
                                    }
                                  : props.discount?.type && props.discount.type !== 'none'
                                  ? {
                                        type: props.discount.type as 'amount' | 'percentage',
                                        value: props.discount.value || 0
                                    }
                                  : undefined,
                          address: props.service.address || '',
                          instructions: props.service.instructions || ''
                      } as TransportationData)
                    : undefined
            };
            setTransportation(transportationFormatted);
        }
        if (props.service && props.flow === 'training') {
            const fromToUse = training?.from || props.date || moment().tz(timezone);
            const toToUse = training?.to || props.dateEnd || moment().tz(timezone);
            const catalogTrainingService = trainingServices?.find(
                service => service.serviceId === props.service!.service.id
            );

            const trainingFormatted: Training = {
                from: fromToUse,
                to: toToUse,
                service: {
                    serviceId: props.service.service.id,
                    serviceName: props.service.service.name,
                    price: props.service.price,
                    duration: training?.service?.duration || props.service.durationInMinutes || 0,
                    color: props.service.color,
                    bookingType: catalogTrainingService?.bookingType ?? props.service.bookingType,
                    maxPets: catalogTrainingService?.maxPets ?? props.service.maxPets,
                    inHomeService: catalogTrainingService?.inHomeService ?? props.service.inHomeService
                } as TrainingService,
                price: training?.price || props.service.price || training?.service?.price || 0,
                saveCustomPrice: training?.saveCustomPrice,
                customPriceId: training?.customPriceId,
                discount: (training?.discount ?? {
                    type: (props.service?.discount?.type === 'none' || props.discount?.type === 'none'
                        ? 'amount'
                        : props.service?.discount?.type || props.discount?.type) as 'amount' | 'percentage',
                    value:
                        props.service?.discount?.type === 'none' || props.discount?.type === 'none'
                            ? 0
                            : (props.service?.discount?.value || props.discount?.value) ?? 0
                }) as ValueTypeSelectorValue,
                staffId: props.staffId,
                address: props.service.address || '',
                instructions: training?.instructions ?? props.service.instructions,
                serviceSessionId: training?.serviceSessionId ?? props.service.serviceSessionId,
                selectedDates: training?.selectedDates?.length
                    ? training.selectedDates
                    : [fromToUse.clone().tz(timezone).startOf('day')]
            };
            setTraining(trainingFormatted);
        }
        if (props.service && props.flow === 'walking') {
            const fromToUse = walking?.from || props.date || moment().tz(timezone);
            const toToUse = walking?.to || props.dateEnd || moment().tz(timezone);
            // Booking type and capacity are not on the appointment, only in the catalog, and
            // they decide whether this service is scheduled from configured sessions at all.
            const catalogWalkingService = walkingServices?.find(
                service => service.serviceId === props.service!.service.id
            );

            const walkingFormatted: Walking = {
                from: fromToUse,
                to: toToUse,
                service: {
                    serviceId: props.service.service.id,
                    serviceName: props.service.service.name,
                    price: props.service.price,
                    duration: walking?.service?.duration || props.service.durationInMinutes || 0,
                    color: props.service.color,
                    inHomeService: catalogWalkingService?.inHomeService ?? props.service.inHomeService,
                    bookingType: catalogWalkingService?.bookingType ?? props.service.bookingType,
                    maxPets: catalogWalkingService?.maxPets ?? props.service.maxPets
                } as WalkingService,
                price: walking?.price || props.service.price || walking?.service?.price || 0,
                saveCustomPrice: walking?.saveCustomPrice,
                customPriceId: walking?.customPriceId,
                discount: (walking?.discount ?? {
                    type: (props.service?.discount?.type === 'none' || props.discount?.type === 'none'
                        ? 'amount'
                        : props.service?.discount?.type || props.discount?.type) as 'amount' | 'percentage',
                    value:
                        props.service?.discount?.type === 'none' || props.discount?.type === 'none'
                            ? 0
                            : (props.service?.discount?.value || props.discount?.value) ?? 0
                }) as ValueTypeSelectorValue,
                staffId: props.staffId,
                address: props.service.address || '',
                instructions: walking?.instructions ?? props.service.instructions,
                serviceSessionId: walking?.serviceSessionId ?? props.service.serviceSessionId,
                selectedDates: walking?.selectedDates?.length
                    ? walking.selectedDates
                    : [fromToUse.clone().tz(timezone).startOf('day')]
            };
            setWalking(walkingFormatted);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceFlowLayoutSyncKey, transportationServices, staffs, walkingServices, trainingServices, customPrices]);

    useEffect(() => {
        dispatch(fetchStaffThunk(apiClientWrapper.marketplaceId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiClientWrapper.marketplaceId]);

    useEffect(() => {
        if (!props.isEvaluation) return;
        if (!props.service) {
            setService(undefined);
            return;
        }
        setService(prev => {
            if (!prev || prev.service?.id !== props.service!.service?.id) {
                return props.service!;
            }
            return prev;
        });
    }, [props.isEvaluation, props.service]);

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            if (defaultDiscount) {
                setDefaultDiscount(undefined);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [defaultDiscount]);

    useEffect(() => {
        if (!petId) return;
        dispatch(searchEnrollmentsThunk(apiClientWrapper, '', undefined, 1, 100, undefined, petId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId]);

    return (
        <>
            {!showFeedingAndMedications && (
                <Box className={classes.headerContainer}>
                    <CreateBookingHeader
                        title={getTitle()}
                        onBack={handlerBack}
                    />
                </Box>
            )}
            <OverlayScrollbarsComponent style={{ height: '100%' }}>
                <Box className={!showFeedingAndMedications ? classes.content : undefined}>
                    <AddOrEditService
                        parentId={props.parentId!}
                        petId={petId}
                        appointmentId={props.appointmentId}
                        service={service}
                        staffId={staffId}
                        staffIdPreselected={props.staffIdPreselected}
                        slotTimes={staffSlotTimes}
                        petsUsed={props.petsUsed}
                        servicesUsed={props.servicesUsed}
                        daycare={daycare}
                        boarding={boarding}
                        transportation={transportation}
                        walking={walking}
                        training={training}
                        versionPetAndServices={props.versionPetAndServices}
                        discount={discount}
                        date={date}
                        dateEnd={props.dateEnd}
                        petWithoutSelect={props.petWithoutSelect || false}
                        defaultDiscount={defaultDiscount}
                        flow={flow || getFlowActive()}
                        recurrentInfo={props.recurrentInfo}
                        nights={props.nights}
                        isEvaluation={props.isEvaluation}
                        showMembershipsTag={showMembershipsTag}
                        membershipsTagLoading={membershipsTagLoading}
                        enrollment={service?.enrollment}
                        enrollmentAnchor={enrollmentAnchor}
                        onChangePet={handlerPet}
                        onChangeDate={setDate}
                        onChangeService={handleChangeService}
                        onChangeStaff={handlerStaff}
                        onChangeDiscount={handlerChangeDiscount}
                        onChangeFlow={setFlow}
                        onShowFeedingAndMedications={setShowFeedingAndMedications}
                        onSaveDaycare={daycare =>
                            props.onSaveDaycare(daycare.daycare!, daycare.feedings!, daycare.medications!, petId!)
                        }
                        onSaveBoarding={boarding =>
                            props.onSaveBoarding(boarding.boarding!, boarding.feedings!, boarding.medications!, petId!)
                        }
                        onBoardingNext={handlerBoardingNext}
                        onDaycareNext={handlerDaycareNext}
                        onDaycareChange={handlerDaycareChange}
                        onBoardingServiceChange={handlerBoardingServiceChange}
                        onBoardingPeriodChange={handlerBoardingPeriodChange}
                        onClearMembership={handlerClearBoardingMembership}
                        onBoardingRoomChanged={handlerBoardingRoomChanged}
                        boardingMembershipAlertServiceId={boardingMembershipAlertContext?.serviceId}
                        onSaveTransportation={transportation => props.onSaveTransportation(transportation, petId!)}
                        onSaveTraining={training => props.onSaveTraining(training, petId!)}
                        onSaveWalking={walking => props.onSaveWalking(walking, petId!)}
                        draftSessionBookings={props.draftSessionBookings}
                        onMembershipServiceSelect={handleMembershipsServiceSelect}
                        membershipPetServicesLoading={
                            (membershipApplyIntent !== null &&
                                newBookingsStatus === NewBookingsStatus.FetchingPetServices) ||
                            (Boolean(membershipApplyIntent?.enrollmentId ?? membershipResolved?.enrollmentId) &&
                                !membershipEnrollmentsReady)
                        }
                        disableMembershipsAutoSelect={props.disableMembershipsAutoSelect}
                        hideServiceTypeTags={props.hideServiceTypeTags}
                    />
                </Box>
            </OverlayScrollbarsComponent>
            {flow !== 'daycare' &&
                flow !== 'boarding' &&
                flow !== 'transportation' &&
                flow !== 'training' &&
                flow !== 'walking' &&
                flow !== 'memberships' && (
                    <Box className={classes.totalContainer}>
                        <FooterComponent
                            title="Total"
                            loading={props.loading || false}
                            hours={service?.duration?.hours ?? props.service?.duration?.hours}
                            minutes={service?.duration?.minutes ?? props.service?.duration?.minutes}
                            buttonText={props.footerButtonText ?? 'Save'}
                            disabledButton={isDisabledButton()}
                            onBook={handlerBook}
                            onRemove={
                                props.versionPetAndServices === versionPetAndServices.edit && !props.hideRemove
                                    ? handleRemoveAppointment
                                    : undefined
                            }
                        />
                    </Box>
                )}
        </>
    );
};

export default PetAndServices;
